import { FC, useMemo, useState } from 'react';
import HashLink from 'components/HashLink';
import InfoTag from 'components/InfoTag';
import SymbolPreviewList, { type SymbolPreviewItem } from 'modules/Transfer/components/SymbolPreviewList';
import { LongShortEnum, SymbolTypeEnum, TransferPropertyInfoItem } from 'modules/Transfer/type';
import { ReactComponent as IconDown } from 'static/images/icons/arrow-down.svg';
import classlist from 'utils/classlist';
import { formatMoney } from 'utils/format';
import { isEmptyValue } from 'utils/helper';
import styles from './style.scss';

interface PartialProps {
    onEditDetails?: () => void;
    transferPropertyInfos: TransferPropertyInfoItem[];
    cashAmount?: number | '';
}

const secTypeToTypeMap: Record<TransferPropertyInfoItem['secType'], SymbolTypeEnum> = {
    STOCK: SymbolTypeEnum.STOCK,
    OPTION: SymbolTypeEnum.OPTION,
    MUTUAL_FUND: SymbolTypeEnum.MUTUAL_FUND
};
const longShortIndToLongShortMap: Record<TransferPropertyInfoItem['longShortInd'], LongShortEnum> = {
    L: LongShortEnum.LONG,
    S: LongShortEnum.SHORT
};

const Partial: FC<PartialProps> = props => {
    const { onEditDetails, transferPropertyInfos, cashAmount } = props;
    const [showAll, setShowAll] = useState(false);
    const shouldShowCash = !isEmptyValue(cashAmount);
    const shouldShowMoreBtn = transferPropertyInfos.length > (shouldShowCash ? 2 : 3);
    const renderSymbolList = useMemo<SymbolPreviewItem[]>(() => {
        const tempSymbolList = transferPropertyInfos.slice(0, showAll ? undefined : shouldShowCash ? 2 : 3);

        return tempSymbolList.map(item => {
            const { symbol, quantity, secType, longShortInd, expiry, putCall, strike } = item;

            const curSymbolItem: SymbolPreviewItem = {
                type: secTypeToTypeMap[secType],
                symbol,
                symbol_name: symbol,
                long_short: longShortIndToLongShortMap[longShortInd],
                quantity: String(quantity),
                secType,
                temporarySymbol: symbol, // 临时的symbol, 用于编辑的时候回显
                expiry,
                putCall,
                strike
            };

            return curSymbolItem;
        });
    }, [transferPropertyInfos, showAll, shouldShowCash]);

    return (
        <div>
            <div>
                <div className={styles.header}>
                    <div className={styles.title}>{__('Transfer Details')}</div>
                    {onEditDetails && <HashLink onClick={onEditDetails}>{__('Edit')}</HashLink>}
                </div>
                <div>
                    {typeof cashAmount !== 'undefined' && shouldShowCash && (
                        <div className={styles.cashItem}>
                            <InfoTag label={__('Cash amount')} content={`${formatMoney(cashAmount)} USD`} />
                        </div>
                    )}
                    <SymbolPreviewList data={renderSymbolList} />
                    {shouldShowMoreBtn && (
                        <div className={styles.moreBtn} onClick={() => setShowAll(oldValue => !oldValue)}>
                            <span>{showAll ? __('Show less') : __('Show more')}</span>
                            <IconDown className={classlist({ [styles.isAll]: !!showAll })} />
                        </div>
                    )}
                </div>
            </div>
            <div>
                <div className={styles.header}>
                    <div className={styles.title}>{__('Transfer:Tips')}</div>
                </div>
                <div className={styles.tipItem}>
                    <div className={styles.tipTitle}>{__('Transfer Fee')}</div>
                    <div className={styles.tipContent}>
                        {__(
                            'The delivering broker may charge a fee, but TradeUP does not impose any additional charges.'
                        )}
                    </div>
                </div>
                <div className={styles.tipItem}>
                    <div className={styles.tipTitle}>{__('Transfer Time')}</div>
                    <div className={styles.tipContent}>
                        {__(
                            'The transfer will settle in 3-5 business days after the delivering broker accepts the transfer request.'
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Partial;
